import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  listCompanies,
  farmsByCompanyID,
  lotsByFarmID,
  lotsByCompanyID,
} from "graphql/queries";
import SelectUser from "components/users/SelectUser";

const STATUS_OPTIONS = [
  "",
  "Semilla",
  "Injerto",
  "Crecimiento",
  "Producción",
  "Espacio",
  "Soca",
  "Otro",
];

const CropUnitForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [form, setForm] = useState(initialValues || {});
  const [companies, setCompanies] = useState({ list: [], loading: false });
  const [farms, setFarms] = useState({ list: [], loading: false });
  const [lots, setLots] = useState({ list: [], loading: false });
  const [errors, setErrors] = useState({
    name: "",
    companyID: "",
    farmID: "",
    lotID: "",
  });

  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
    farmID: "isRequired",
    lotID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(form).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;
    if (checkIncorrectValue("farmID", farms.list)) return;
    if (checkIncorrectValue("lotID", lots.list)) return;

    if (!hasErrors) onSubmit(form);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === form[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const selectResponsible = (responsibleID) => {
    setForm((prevSt) => ({ ...prevSt, performedById: responsibleID }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    if (name === "farmID") {
      if (form.companyID)
        fetchFarms({ companyID: form.companyID, name: value });
      // when typing farm name also refresh lots for that farm if company present
      if (form.companyID)
        fetchLots({
          companyID: form.companyID,
          farmID: form.farmID,
          name: value,
        });
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    if (name === "companyID") {
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
        farmID: "",
        lotID: "",
      }));
      fetchFarms({ companyID: value });
      setLots({ list: [], loading: false });
      checkErrors(name, value);
      return;
    }
    if (name === "farmID") {
      setForm((prevSt) => ({ ...prevSt, [name]: value, lotID: "" }));
      fetchLots({ companyID: form.companyID || me?.companyID, farmID: value });
      checkErrors(name, value);
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      setFarms({ list: [], loading: false });
      setLots({ list: [], loading: false });
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
        farmID: "",
        lotID: "",
      }));
      return;
    }
    if (name === "farmID") {
      if (form.companyID) fetchFarms({ companyID: form.companyID, name: "" });
      setLots({ list: [], loading: false });
      setForm((prevSt) => ({ ...prevSt, [name]: value, lotID: "" }));
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({ name: "", companyID: "", farmID: "", lotID: "" });
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    fetchFarms({
      companyID: initialValues?.companyID,
      id: initialValues?.farmID,
    });
    fetchLots({
      companyID: initialValues?.companyID,
      farmID: initialValues?.farmID,
      id: initialValues?.lotID,
    });
  };

  async function fetchFarms({ companyID = "", id = "", name = "" }) {
    try {
      setFarms({ list: [], loading: true });
      if (!companyID) {
        setFarms({ list: [], loading: false });
        return;
      }
      const variables = { companyID };
      if (name) variables.filter = { name: { contains: name } };
      if (id) variables.filter = { id: { eq: id } };

      const farmsData = await API.graphql(
        graphqlOperation(farmsByCompanyID, variables)
      );
      const farmsItems = farmsData.data.farmsByCompanyID.items;
      setFarms((prev) => ({ ...prev, list: farmsItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setFarms((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchLots({
    companyID = "",
    farmID = "",
    id = "",
    name = "",
  }) {
    try {
      setLots({ list: [], loading: true });
      if (!companyID && !farmID) {
        setLots({ list: [], loading: false });
        return;
      }
      let variables = {};
      if (farmID) {
        variables = { farmID };
        if (name) variables.filter = { name: { contains: name } };
        if (id) variables.filter = { id: { eq: id } };
        const lotsData = await API.graphql(
          graphqlOperation(lotsByFarmID, variables)
        );
        const lotsItems = lotsData.data.lotsByFarmID.items;
        setLots((prev) => ({ ...prev, list: lotsItems }));
        return;
      }
      // fallback to company-level lots
      if (companyID) {
        variables = { companyID };
        if (name) variables.filter = { name: { contains: name } };
        if (id) variables.filter = { id: { eq: id } };
        const lotsData = await API.graphql(
          graphqlOperation(lotsByCompanyID, variables)
        );
        const lotsItems = lotsData.data.lotsByCompanyID.items;
        setLots((prev) => ({ ...prev, list: lotsItems }));
        return;
      }
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setLots((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me?.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me?.managedCompanies?.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [{ id: { contains: id } }, { name: { contains: name } }],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    fetchFarms({
      companyID: initialValues?.companyID,
      id: initialValues?.farmID,
    });
    fetchLots({
      companyID: initialValues?.companyID,
      farmID: initialValues?.farmID,
      id: initialValues?.lotID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={form.name || ""}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          />
          <TextField
            name="plantingDate"
            label={strings.fields.plantingDate.label}
            value={form.plantingDate || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          {type === "create" && (
            <Flex direction="column" gap="xs">
              <Label htmlFor="companyID">
                <LabelRequired text={strings.fields.company.label} />
              </Label>
              <Autocomplete
                id="companyID"
                name="companyID"
                isRequired
                placeholder={strings.fields.company.placeholder}
                value={
                  companies.list.find(
                    (companyRec) => companyRec.id === form.companyID
                  )?.name ??
                  form.companyID ??
                  ""
                }
                options={companies.list.map((companyRec) => ({
                  id: companyRec.id,
                  label: companyRec.name,
                }))}
                isLoading={companies.loading}
                onClear={() => onClearSelect("companyID")}
                onChange={onChangeSelect}
                onSelect={({ id }) => onSelect("companyID", id)}
                errorMessage={errors.companyID}
                hasError={!!errors.companyID}
                menuSlots={{ Empty: globalStrings.noResults }}
              />
            </Flex>
          )}

          <Flex direction="column" gap="xs">
            <Label htmlFor="farmID">
              <LabelRequired text={strings.fields.farm.label} />
            </Label>
            <Autocomplete
              id="farmID"
              name="farmID"
              disabled={!form.companyID}
              isRequired
              placeholder={strings.fields.farm.placeholder}
              value={
                farms.list.find((f) => f.id === form.farmID)?.name ??
                form.farmID ??
                ""
              }
              options={farms.list.map((rec) => ({
                id: rec.id,
                label: rec.name,
              }))}
              isLoading={farms.loading}
              onClear={() => onClearSelect("farmID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("farmID", id)}
              errorMessage={errors.farmID}
              hasError={!!errors.farmID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
          <Flex direction="column" gap="xs">
            <Label htmlFor="lotID">
              <LabelRequired text={strings.fields.lot.label} />
            </Label>
            <Autocomplete
              id="lotID"
              name="lotID"
              disabled={!form.companyID || !form.farmID}
              isRequired
              placeholder={strings.fields.lot.placeholder}
              value={
                lots.list.find((l) => l.id === form.lotID)?.name ??
                form.lotID ??
                ""
              }
              options={lots.list.map((rec) => ({
                id: rec.id,
                label: rec.name,
              }))}
              isLoading={lots.loading}
              onClear={() => onClearSelect("lotID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("lotID", id)}
              errorMessage={errors.lotID}
              hasError={!!errors.lotID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
          <div>
            <Label style={{ display: "block", marginBottom: 6 }}>
              {strings.fields.responsible.label}
            </Label>
            <SelectUser
              value={form.performedById}
              placeholder={strings.fields.responsible.label}
              onSelect={selectResponsible}
            />
          </div>
          <TextField
            name="cropType"
            label={strings.fields.cropType.label}
            value={form.cropType || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="unitType"
            label={strings.fields.unitType.label}
            value={form.unitType || ""}
            onChange={onChange}
          />
          <TextField
            name="quantity"
            label={strings.fields.quantity.label}
            value={form.quantity || ""}
            onChange={onChange}
          />
        </Grid>

        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={form.description || ""}
          onChange={onChange}
        />

        <Grid
          templateColumns={{ base: "1fr", large: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="photoEvidence"
            label={strings.fields.photoEvidence.label}
            placeholder="foto1.jpg, foto2.jpg"
            value={
              Array.isArray(form.photoEvidence)
                ? form.photoEvidence.join(", ")
                : form.photoEvidence || ""
            }
            onChange={(e) =>
              setForm({ ...form, photoEvidence: e.target.value })
            }
          />
          <SelectField
            name="status"
            label={strings.fields.status.label}
            value={form.status || ""}
            onChange={onChange}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </SelectField>
        </Grid>

        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          />
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          />
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: { label: "Nombre" },
      plantingDate: { label: "Fecha de siembra" },
      cropType: { label: "Tipo de cultivo" },
      unitType: { label: "Tipo de unidad" },
      quantity: { label: "Cantidad" },
      description: { label: "Descripción" },
      company: { label: "Empresa", placeholder: "Buscar empresa..." },
      farm: { label: "Finca", placeholder: "Buscar finca..." },
      lot: { label: "Lote", placeholder: "Buscar lote..." },
      photoEvidence: { label: "Evidencia fotográfica" },
      status: { label: "Estado" },
      responsible: { label: "Persona responsable" },
      buttons: { create: "Crear", save: "Guardar", reset: "Restablecer" },
    },
    validations: { incorrectValue: "Valor incorrecto" },
    responses: { errorFetchCompanies: "Error al consultar empresas" },
  },
});

export default CropUnitForm;
